import { useState, useEffect } from 'react';
import {
  GiftIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  ClipboardIcon,
  ShareIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

const ReferAndEarn = () => {
  const [referralData, setReferralData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchReferralData();
    fetchReferralSettings();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get('/api/referrals/profile');
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    }
  };

  const fetchReferralSettings = async () => {
    try {
      const response = await axios.get('/api/referrals/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching referral settings:', error);
    }
  };

  const loadingComplete = () => {
    return referralData !== null && settings !== null;
  };

  useEffect(() => {
    if (loadingComplete()) {
      setLoading(false);
    }
  }, [referralData, settings]);

  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(referralData?.user?.referralCode || '');
      setCopySuccess(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      toast.error('Failed to copy referral code');
    }
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralCode = referralData?.user?.referralCode;
    return `${baseUrl}/registration?ref=${referralCode}`;
  };

  const shareReferralLink = async () => {
    const link = getReferralLink();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Osmosis Computer LMS',
          text: `Join me on Osmosis Computer LMS! Use my referral code and get ₹50 off on your first course. Sign up here:`,
          url: link
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Referral link copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy referral link');
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'registered':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'enrolled':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <UserPlusIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'registered':
        return 'Registered';
      case 'enrolled':
        return 'Enrolled & Earned';
      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center">
          <GiftIcon className="h-8 w-8 mr-3" />
          <div>
            <h1 className="text-2xl font-bold">Refer & Earn</h1>
            <p className="text-blue-100">Invite friends and earn rewards together!</p>
          </div>
        </div>
      </div>

      {/* Wallet Balance */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Your Wallet Balance</h2>
            <p className="text-3xl font-bold text-green-600 flex items-center mt-2">
              <CurrencyRupeeIcon className="h-8 w-8 mr-1" />
              {referralData?.user?.walletBalance || 0}
            </p>
          </div>
          <CurrencyRupeeIcon className="h-16 w-16 text-green-500 opacity-20" />
        </div>
      </div>

      {/* Referral Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Share Your Referral Link</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Referral Link */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Your Referral Link
            </label>
            <div className="flex">
              <input
                type="text"
                value={getReferralLink()}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={shareReferralLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
              >
                <ShareIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Referral Code */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Your Referral Code
            </label>
            <div className="flex">
              <input
                type="text"
                value={referralData?.user?.referralCode || ''}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <button
                onClick={copyReferralCode}
                className={`px-4 py-2 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 ${
                  copySuccess
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <ClipboardIcon className="h-5 w-5" />
              </button>
            </div>
            {copySuccess && (
              <p className="text-sm text-green-600">Code copied to clipboard!</p>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">How it works:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Share your unique referral link or code with friends</li>
            <li>• When they sign up and enroll in a course, you both earn money!</li>
            <li>• You earn <span className="font-semibold text-green-700">₹{settings?.referrerEarning || 100}</span> when your referral enrolls in a course</li>
            <li>• They get <span className="font-semibold text-green-700">₹{settings?.referredUserEarning || 50}</span> bonus in their wallet</li>
            <li>• Bonuses are credited immediately upon successful course enrollment</li>
            <li>• Use wallet balance to get discounts on future courses</li>
          </ul>
        </div>
      </div>

      {/* Referral Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Statistics</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{referralData?.stats?.totalReferrals || 0}</div>
            <div className="text-sm text-gray-500">Total Referrals</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{referralData?.stats?.registeredUsers || 0}</div>
            <div className="text-sm text-gray-500">Registered Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{referralData?.stats?.enrolledUsers || 0}</div>
            <div className="text-sm text-gray-500">Enrolled Users</div>
          </div>
        </div>

        {/* Referral History */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Referral History</h3>

          {referralData?.referrals?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserGroupIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No referrals yet. Start sharing your referral link!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {referralData?.referrals?.map((referral) => (
                <div key={referral._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(referral.status)}
                    <div>
                      <div className="font-medium text-gray-900">
                        {referral.referredUser.firstName} {referral.referredUser.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {referral.referredUser.email}
                      </div>
                      {referral.enrolledCourse && (
                        <div className="text-sm text-green-600">
                          Enrolled in: {referral.enrolledCourse.title}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      referral.status === 'enrolled'
                        ? 'bg-green-100 text-green-800'
                        : referral.status === 'registered'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatusText(referral.status)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </div>
                    {referral.earned && (
                      <div className="text-sm font-medium text-green-600">
                        Earned ₹{settings?.referralSettings?.referrerEarning || 100}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferAndEarn;
